import {useMessage} from '@coolcolduk/ui-mui';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography} from '@mui/material';
import {useCallback, useState} from 'react';
import {parseAiGeneratedQuestion} from '../../pages/admin/questionNew/util/parseAiGeneratedQuestion';
import {QuestionFormData} from '../../types/QuestionFormData';

export interface PasteQuestionDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (formData: Partial<QuestionFormData>) => void;
}

export default function PasteQuestionDialog({open, onClose, onApply}: PasteQuestionDialogProps) {
  const {showMessage} = useMessage();
  const [aiOutput, setAiOutput] = useState('');

  const handleAiOutputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAiOutput(e.target.value);
  }, []);

  const handleApply = useCallback(() => {
    if (!aiOutput.trim()) {
      showMessage('Please paste the AI-generated output', 6000, 'error');
      return;
    }

    try {
      const parsed = parseAiGeneratedQuestion(aiOutput);
      onApply(parsed);
      showMessage('Question applied successfully!', 6000, 'success');
      setAiOutput('');
      onClose();
    } catch (error) {
      console.error('Error parsing AI output:', error);
      showMessage(
        `Failed to parse AI output: ${error instanceof Error ? error.message : 'Unknown error'}`,
        6000,
        'error',
      );
    }
  }, [aiOutput, onApply, onClose, showMessage]);

  const handleClose = useCallback(() => {
    setAiOutput('');
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{fontWeight: 600, fontSize: '1.25rem'}}>Paste Pre-Generated Question</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{pt: 2}}>
          <Typography variant="body2" color="text.secondary">
            Paste a pre-generated question JSON here. The question will be applied to the form without requiring module
            and topic selection.
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={12}
            value={aiOutput}
            onChange={handleAiOutputChange}
            placeholder="Paste the AI-generated question output here (JSON or JSONL format)..."
            sx={{fontFamily: 'monospace', fontSize: '0.875rem'}}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={!aiOutput.trim()}
          sx={{
            bgcolor: '#FF6F59',
            '&:hover': {bgcolor: '#FF6F59', opacity: 0.8},
          }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
